import classNames from "classnames";
import i18next from "i18next";
import { html } from "lit-html";
import { MenuItemId, MenuItemWithDropdownContent } from "./header";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { isMobileResolution } from "Services/common";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { useCultureSelector } from "./useCultureSelector";
import { useLoggedInUserPill } from "./useLoggedInUserPill";
import { useReduxState } from "Shared/redux/useReduxState";
import { useMobileMenuDropdown } from "./useMobileMenuDropdown";
import { iconButton } from "Components/iconButton";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { useLogoutHandler } from "ComponentHelpers/logoutHelper";

export interface Props {
	absoluteAction: string;
	activeMenuItemId: MenuItemId;
	bookingUrl: string;
	culture: string;
	dropdownMenus: MenuItemWithDropdownContent[];
	dynamicSettings: DynamicSettings;
	isCollapsed: boolean;
	showMenu: boolean;
	closeDropdown: () => void;
	closeLoginWidget: () => void;
	onRegisterAccountClick: () => void;
	openDropdown: (itemId: MenuItemId) => void;
	toggleLoginWidget: () => void;
	toggleMenuItem: (itemId: MenuItemId) => void;
}

export const useMenubar = (props: Props) => {
	const [userInfo] = useReduxState("userInfo");

	const { handleLogout } = useLogoutHandler({
		absoluteAction: props.absoluteAction,
		bookingUrl: props.bookingUrl,
		culture: props.culture,
	});

	const userPill = useLoggedInUserPill({
		absoluteAction: props.absoluteAction,
		bookingUrl: props.bookingUrl,
		culture: props.culture,
		onMouseEnter: () => {
			props.closeDropdown();
			mobileMenubar.closeMobileMenu();
		},
		handleLogout,
	});

	const languageAndCountry = (culture = props.culture) => {
		if (!culture) return { language: "es", country: "cl" };

		const [language, country] = culture.split("-");

		return { language: language || "es", country: country || "cl" };
	};

	const cultureSelector = useCultureSelector({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isHeaderCollapsed: props.isCollapsed,
		onOpen: () => {
			props.closeLoginWidget();
			props.closeDropdown();
		},
	});

	const handleMenuItemMouseEnter = (itemId: MenuItemId) => {
		props.closeLoginWidget();
		cultureSelector.close();
		props.openDropdown(itemId);
	};

	const handleMobileMenuClick = () => {
		cultureSelector.close();
		props.closeLoginWidget();
		mobileMenubar.toggleMobileMenu();
	};

	const toggleLoginWidget = () => {
		cultureSelector.close();
		props.closeDropdown();
		mobileMenubar.closeMobileMenu();
		props.toggleLoginWidget();
	};

	const loginButton = iconButton({
		dataTestId: TestIdDictionary.Menu.LoginOpener,
		icon: html`<i class="jsh-icon jsh-login ml-2 text-base"></i>`,
		isAction: false,
		label: i18next.t("layout-login"),
		onClick: toggleLoginWidget,
	});

	const mobileMenubar = useMobileMenuDropdown({
		absoluteAction: props.absoluteAction,
		activeMenuItemId: props.activeMenuItemId,
		bookingUrl: props.bookingUrl,
		culture: props.culture,
		dropdownMenus: props.dropdownMenus,
		dynamicSettings: props.dynamicSettings,
		isCollapsed: props.isCollapsed,
		closeLoginWidget: props.closeLoginWidget,
		onRegisterAccountClick: props.onRegisterAccountClick,
		toggleMenuItem: props.toggleMenuItem,
	});

	const logoImageTemplate = () => html`
		<img
			class="ml-6 max-h-9 common-transition md:ml-0 lg:max-h-10.5"
			src="/images/header/jetsmart-logo-colored.svg"
			alt="JetSMART"
		/>
	`;

	const logoLinkTemplate = () => {
		const { language, country } = languageAndCountry();
		const baseUrl = `/${country}/${language}`;

		return html`
			<a
				class="m-0 mr-2.75 flex w-27.5 cursor-pointer items-center common-transition lg:h-21.5 xl:w-45"
				href=${baseUrl}
				@mouseenter=${props.closeDropdown}
			>
				${logoImageTemplate()}
			</a>
		`;
	};

	const menuItemTemplate = (item: MenuItemWithDropdownContent) => html`
		<li
			class=${classNames("h-full cursor-pointer", {
				hidden: userInfo?.AmericanAirlines?.IsMember && isMobileResolution(),
			})}
			@mouseenter=${() => handleMenuItemMouseEnter(item.id)}
		>
			<div
				class=${classNames(
					"max-w-fit flex gap-1 items-center h-full hover:underline hover:underline-offset-2",
					{
						"underline underline-offset-2": props.activeMenuItemId === item.id,
					}
				)}
			>
				<span class="mr-1 text-base md:text-sm lg:text-base">${unsafeHTML(item.title)}</span>
				<span
					class="border-x-[5px] border-b-0 border-t-[5px] border-solid border-x-transparent border-t-[#204071]"
				></span>
			</div>
		</li>
	`;

	const menuForAnonymousUsersTemplate = () =>
		!userInfo?.Name
			? html`
					<div class="flex h-full items-center" @mouseenter=${props.closeDropdown}>
						${loginButton.htmlTemplate()}
					</div>
				`
			: "";

	const menuItemsTemplate = () => html`
		<ul
			class=${classNames(
				"hidden list-none text-n-blue h-[55px] items-center gap-2",
				"md:flex",
				"lg:h-[86px] lg:gap-4",
				"xl:gap-6"
			)}
		>
			${props.dropdownMenus.map(menuItemTemplate)}
			<li class=${classNames({ "hidden": userInfo?.Name, "block h-full": !userInfo?.Name })}>
				${menuForAnonymousUsersTemplate()}
			</li>
			${userPill.htmlTemplate()}
		</ul>
	`;

	const hamburgerButtonTemplate = () => html`
		<div
			class="absolute right-0 flex h-7.5 w-5 cursor-pointer flex-col items-center justify-center gap-1 md:hidden"
			aria-label="Toggle menu"
			@click=${handleMobileMenuClick}
		>
			<span class="h-0.5 w-5 bg-[#a62733]"></span>
			<span class="h-0.5 w-5 bg-[#a62733]"></span>
			<span class="h-0.5 w-5 bg-[#a62733]"></span>
		</div>
	`;

	const mobileLoginTemplate = () => html`<div class="mr-9 md:hidden">${menuForAnonymousUsersTemplate()}</div>`;

	const desktopCultureSelectorTemplate = () => html`
		<div
			class=${classNames("ml-4 hidden md:block xl:ml-8 cursor-pointer", {
				"md:h-[55px] lg:h-[86px]": !props.isCollapsed,
			})}
		>
			${cultureSelector.htmlTemplate()}
		</div>
	`;

	const menuTemplate = () =>
		props.showMenu
			? html`
					<div class="relative flex items-center" @mousemove=${(e: MouseEvent) => e.stopPropagation()}>
						<div class="md:hidden">${userPill.htmlTemplate()}</div>
						${hamburgerButtonTemplate()} ${menuItemsTemplate()} ${desktopCultureSelectorTemplate()}
						${mobileLoginTemplate()}
					</div>
				`
			: "";

	const htmlTemplate = () => html`
		<div class="flex w-full justify-center border-b-4 border-solid border-[#a62733] bg-white">
			<section
				class=${classNames("flex select-none items-center justify-between common-transition container", {
					"h-[55px] lg:h-[86px]": !props.isCollapsed,
				})}
				@mousemove=${props.closeDropdown}
			>
				${logoLinkTemplate()} ${menuTemplate()}
			</section>
		</div>
		${mobileMenubar.htmlTemplate()}
	`;

	return { closeMobileMenu: mobileMenubar.closeMobileMenu, htmlTemplate };
};
